import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { buttonClasses, PageHeader } from '@/components/ui';
import { useVerifyMagicLink } from '@/lib/queries';

/**
 * Spends a magic link (login, or the confirmation email's, record 0026) with a POST once the
 * page has loaded, so email link scanners that only GET it don't use it up. A used or expired
 * link offers a new one instead of a dead end (D4).
 */
export function Verify() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const verify = useVerifyMagicLink();
  const started = useRef(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    // Once: a second POST of the same single-use token would fail.
    if (started.current) return;
    started.current = true;
    const token = params.get('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    verify
      .mutateAsync(token)
      .then(async ({ redirectTo }) => {
        await qc.invalidateQueries({ queryKey: ['me'] });
        navigate(redirectTo, { replace: true });
      })
      .catch(() => setExpired(true));
  }, []);

  if (!expired) return <p className="text-center text-ink-muted">…</p>;
  return (
    <section className="flex flex-1 flex-col gap-7 pt-14">
      <PageHeader title={t('verify.expired.title')} intro={t('verify.expired.body')} />
      <Link to="/login" className={buttonClasses('primary')}>
        {t('verify.expired.action')}
      </Link>
    </section>
  );
}
